/**
 * Pure helpers for the chart card pattern (Paper → App → Foundations,
 * "Charts · Card pattern · Oct 2026"). Kept free of React so they are unit tested.
 */

/**
 * Four evenly spaced Y ticks from zero, on a 1 / 2 / 2.5 / 3 / 5 step: 1,200 →
 * 0, 500, 1K, 1.5K; $805 → $0, $300, $600, $900. Always finite and rising, so
 * the axis domain never goes NaN.
 */
export function niceTicks(max: number, count = 4): number[] {
  const intervals = Math.max(1, count - 1)
  if (!Number.isFinite(max) || max <= 0) return Array.from({ length: count }, (_, i) => i)
  const raw = max / intervals
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 3, 5, 10].map((m) => m * magnitude).find((s) => s * intervals >= max) ?? 10 * magnitude
  // Steps under 1 only make sense for money below a dollar; round away float noise.
  return Array.from({ length: count }, (_, i) => Number((step * i).toPrecision(12)))
}

/**
 * Signed change with one decimal and a true minus: "+15.0%", "−1.0%".
 * Null when there is nothing honest to compare against (no previous value, or 0).
 */
export function formatSignedDelta(current: number | null | undefined, previous: number | null | undefined): string | null {
  if (current == null || previous == null) return null
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null
  const pct = ((current - previous) / previous) * 100
  const rounded = Math.round(pct * 10) / 10
  if (rounded === 0) return '0.0%'
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(1)}%`
}

/** Legend and tooltip names for the two series, by the selected range. */
export function periodSeriesLabels(period: string): { current: string; previous: string } {
  switch (period) {
    case '1d':
    case 'PAST_24_H':
      return { current: 'Last 24 hours', previous: 'Previous 24 hours' }
    case '7d':
    case 'LAST_7_DAYS':
      return { current: 'This week', previous: 'Last week' }
    case 'mtd':
    case 'MONTH_TO_DATE':
      return { current: 'This month', previous: 'Last month' }
    case 'ytd':
    case 'YEAR_TO_DATE':
      return { current: 'This year', previous: 'Last year' }
    default:
      return { current: 'This period', previous: 'Previous period' }
  }
}

/** "Sep 12 to Sep 18" for a window of ISO instants; one date when they share a day. */
export function formatWindowRange(start: string | null | undefined, end: string | null | undefined): string | null {
  if (!start || !end) return null
  const s = new Date(start)
  const e = new Date(end)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const a = fmt(s)
  const b = fmt(e)
  return a === b ? a : `${a} to ${b}`
}
